import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { useSelector } from "react-redux";
import { ToastContainer } from "react-toastify";
import { DocumentRequirementsProvider } from "./context/DocumentRequirementsContext";
import { GoogleMapsProvider } from "./context/GoogleMapsContext";
import "react-toastify/dist/ReactToastify.css";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./assets/layout-utils.css";

// Layouts
import Header from "./layouts/Header";
import Footer from "./layouts/Footer";
import AdminLayout from "./layouts/AdminLayout";

// Public Pages
import Home from "./pages/Home";
import OneWay from "./pages/OneWay";
import RoundTrip from "./pages/RoundTrip";
import Login from "./pages/Login";
import Register from "./pages/Register";
import BookingTrack from "./pages/BookingTrack";
import NotFound from "./pages/NotFound";
import CarSelection from "./pages/CarSelection";
import CustomerDetails from "./pages/CustomerDetails";
import PaymentPage from "./pages/PaymentPage";
import ClearStorage from "./pages/ClearStorage";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import Refund from "./pages/Refund";



import RouteRedirect from "./components/RouteRedirect";

// Protected Pages
import Dashboard from "./pages/Dashboard";
import BookingHistory from "./pages/BookingHistory";
import Bookings from "./pages/Bookings";
import BookingDetails from "./pages/BookingDetails";

import Profile from "./pages/Profile";
import Payment from "./pages/Payment";
import PaymentSuccess from "./pages/PaymentSuccess";

// Admin Pages
import AdminLogin from "./pages/Admin/AdminLogin";
import AdminDashboard from "./pages/Admin/Dashboard";
import AdminUsers from "./pages/Admin/Users";
import AdminBookings from "./pages/Admin/Bookings";
import AdminCabs from "./pages/Admin/Cabs";
import AdminVehicleLocations from "./pages/Admin/VehicleLocations";
import AdminDriverManagement from "./pages/Admin/DriverManagement";
import AdminBookingAssignment from "./pages/Admin/BookingAssignment";
import DocumentConfiguration from "./pages/Admin/DocumentConfiguration";
import PopularRouteManagement from "./pages/Admin/PopularRouteManagement";
import PopularCities from "./pages/Admin/PopularCities";
import FooterRoutes from "./pages/Admin/FooterRoutes";
import Airports from "./pages/Admin/Airports";
import RentalPackages from "./pages/Admin/RentalPackages";
import WalletSummary from "./pages/Admin/WalletSummary";
import WalletDrivers from "./pages/Admin/WalletDrivers";
import WalletTransactions from "./pages/Admin/WalletTransactions";
import PendingWithdrawals from "./pages/Admin/PendingWithdrawals";
import CouponManagement from "./pages/Admin/CouponManagement";
import VendorManagement from "./pages/Admin/VendorManagement";
import CommissionManagement from "./pages/Admin/CommissionManagement";


// Driver Pages
import DriverLogin from "./pages/driver/DriverLogin";
import DriverRegister from "./pages/driver/DriverRegister";
import ForgotPassword from "./pages/ForgotPassword";
import SendOtp from "./pages/forgot-password/SendOtp";
import ResetPassword from "./pages/forgot-password/ResetPassword";
import DriverDashboard from "./pages/driver/DriverDashboard";
import DriverProfile from "./pages/driver/DriverProfile";
import CurrentRide from "./pages/driver/CurrentRide";
import RideHistory from "./pages/driver/RideHistory";
import WalletWithdraw from "./pages/driver/WalletWithdraw";
import WalletHistory from "./pages/driver/WalletHistory";
import WalletCommission from "./pages/driver/WalletCommission";

// Vendor Pages
import VendorLogin from "./pages/vendor/VendorLogin";
import VendorRegister from "./pages/vendor/VendorRegister";
import VendorDashboard from "./pages/vendor/VendorDashboard";
import VendorSetPassword from "./pages/vendor/VendorSetPassword";
import VendorDriverAdd from "./pages/vendor/VendorDriverAdd";
import VendorWalletHistory from "./pages/vendor/VendorWalletHistory";
import VendorWithdraw from "./pages/vendor/VendorWithdraw";

// Components
import PrivateRoute from "./components/PrivateRoute";
import AdminRoute from "./components/AdminRoute";
import DriverRoute from "./components/DriverRoute";
import VendorRoute from "./components/VendorRoute";
import ScrollToTop from "./components/ScrollToTop";
import DriverHeader from "./layouts/DriverHeader";
import DriverAuthMonitor from "./components/DriverAuthMonitor";

// Google Maps API configuration
const googleMapsConfig = {
  googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  libraries: ["places", "geometry"],
  id: "google-maps-script",
  preventGoogleFontsLoading: true,
  version: "beta",
  language: "en",
  region: "IN",
};

function App() {
  // Initialize socket connection once at the app level
  useEffect(() => {
    // Import socket service directly to avoid issues with hooks
    const socketService = require("./utils/socketService").default;

    // Connect to socket server at the application level to maintain a single connection
    socketService.connect();

    return () => {
      // Only disconnect on full app unmount (rarely happens in production)
      socketService.disconnect();
    };
  }, []);

  return (
    <GoogleMapsProvider options={googleMapsConfig}>
      <DocumentRequirementsProvider>
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <ScrollToTop />
          <DriverAuthMonitor />
          <AppContent />
          <ToastContainer position="top-right" autoClose={5000} />
        </Router>
      </DocumentRequirementsProvider>
    </GoogleMapsProvider>
  );
}

// Component to conditionally render the header based on the current route
function AppContent() {
  // Use the actual useLocation hook from react-router-dom
  const location = useLocation();
  const pathname = location.pathname;
  const { driver } = useSelector((state) => state.driverAuth);

  // Check if we're on any driver page or if the user is logged in as a driver
  const isDriverPage =
    pathname.includes("/driver/dashboard") ||
    pathname.includes("/driver/current-ride") ||
    pathname.includes("/driver/ride-history") ||
    pathname.includes("/driver/profile") ||
    pathname.includes("/driver/wallet");


  return (
    <div className="flex flex-col min-h-screen overflow-x-hidden">
      {/* Show Driver Header on driver pages, main Header on other pages except admin and vendor pages */}
      {isDriverPage ? (
        <DriverHeader />
      ) : (
        !pathname.includes("/admin") && !pathname.includes("/vendor") && <Header />
      )}

      <main className={`flex-grow ${(!pathname.includes("/admin") && !pathname.includes("/vendor")) ? 'page-container' : ''}`}>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Home />} />
          <Route path="/route/:route" element={<RouteRedirect />} />
          <Route path="/city/one-way-cab" element={<OneWay />} />
          <Route path="/city/round-trip-cab" element={<RoundTrip />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/:role/change-password" element={<ForgotPassword />} />
          <Route path="/user/forgot-password/send-otp" element={<SendOtp />} />
          <Route path="/user/reset-password" element={<ResetPassword />} />
          <Route path="/driver/forgot-password/send-otp" element={<SendOtp role="driver" />} />
          <Route path="/driver/reset-password" element={<ResetPassword role="driver" />} />
          <Route path="/vendor/forgot-password/send-otp" element={<SendOtp role="vendor" />} />
          <Route path="/vendor/reset-password" element={<ResetPassword role="vendor" />} />
          <Route path="/admin/forgot-password/send-otp" element={<SendOtp role="admin" />} />
          <Route path="/admin/reset-password" element={<ResetPassword role="admin" />} />
          <Route path="/track-booking" element={<BookingTrack />} />
          <Route path="/cabs" element={<CarSelection />} />
          <Route
            path="/booking/customer-details"
            element={<CustomerDetails />}
          />
          <Route path="/booking/payment" element={<PaymentPage />} />
          <Route path="/driver/login" element={<DriverLogin />} />
          <Route path="/driver/register" element={<DriverRegister />} />
                    <Route path="/vendor/login" element={<VendorLogin />} />
          <Route path="/vendor/register" element={<VendorRegister />} />
          <Route path="/vendor/set-password" element={<VendorSetPassword />} />
          <Route
            path="/vendor/dashboard"
            element={
              <VendorRoute>
                <VendorDashboard />
              </VendorRoute>
            }
          />
          <Route
            path="/vendor/drivers/add"
            element={
              <VendorRoute>
                <VendorDriverAdd />
              </VendorRoute>
            }
          />
          <Route
            path="/vendor/wallet/history"
            element={
              <VendorRoute>
                <VendorWalletHistory />
              </VendorRoute>
            }
          />
          <Route
            path="/vendor/wallet/withdraw"
            element={
              <VendorRoute>
                <VendorWithdraw />
              </VendorRoute>
            }
          />
          <Route path="/clear-storage" element={<ClearStorage />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/refund" element={<Refund />} />




          {/* Protected Routes */}
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <Dashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/bookings"
            element={
              <PrivateRoute>
                <Bookings />
              </PrivateRoute>
            }
          />
          <Route
            path="/booking/:id"
            element={
              <PrivateRoute>
                <BookingDetails />
              </PrivateRoute>
            }
          />

          <Route
            path="/profile"
            element={
              <PrivateRoute>
                <Profile />
              </PrivateRoute>
            }
          />
          <Route
            path="/payment/:bookingId"
            element={
              <PrivateRoute>
                <Payment />
              </PrivateRoute>
            }
          />
          <Route
            path="/payment-success/:bookingId"
            element={
              <PrivateRoute>
                <PaymentSuccess />
              </PrivateRoute>
            }
          />

          {/* Admin Routes */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <AdminLayout>
                  <AdminDashboard />
                </AdminLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/dashboard"
            element={
              <AdminRoute>
                <AdminLayout>
                  <AdminDashboard />
                </AdminLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <AdminRoute>
                <AdminLayout>
                  <AdminUsers />
                </AdminLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/bookings"
            element={
              <AdminRoute>
                <AdminLayout>
                  <AdminBookings />
                </AdminLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/cabs"
            element={
              <AdminRoute>
                <AdminLayout>
                  <AdminCabs />
                </AdminLayout>
              </AdminRoute>
            }
          />

          <Route
            path="/admin/vehicle-locations"
            element={
              <AdminRoute>
                <AdminLayout>
                  <AdminVehicleLocations />
                </AdminLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/drivers"
            element={
              <AdminRoute>
                <AdminLayout>
                  <AdminDriverManagement />
                </AdminLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/vendors"
            element={
              <AdminRoute>
                <AdminLayout>
                  <VendorManagement />
                </AdminLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/commission"
            element={
              <AdminRoute>
                <AdminLayout>
                  <CommissionManagement />
                </AdminLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/booking-assignment"
            element={
              <AdminRoute>
                <AdminLayout>
                  <AdminBookingAssignment />
                </AdminLayout>
              </AdminRoute>
            }
          />

          <Route
            path="/admin/document-settings"
            element={
              <AdminRoute>
                <AdminLayout>
                  <DocumentConfiguration />
                </AdminLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/popular-routes"
            element={
              <AdminRoute>
                <AdminLayout>
                  <PopularRouteManagement />
                </AdminLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/popular-cities"
            element={
              <AdminRoute>
                <AdminLayout>
                  <PopularCities />
                </AdminLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/footer-routes"
            element={
              <AdminRoute>
                <AdminLayout>
                  <FooterRoutes />
                </AdminLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/airports"
            element={
              <AdminRoute>
                <AdminLayout>
                  <Airports />
                </AdminLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/rental-packages"
            element={
              <AdminRoute>
                <AdminLayout>
                  <RentalPackages />
                </AdminLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/wallet/summary"
            element={
              <AdminRoute>
                <AdminLayout>
                  <WalletSummary />
                </AdminLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/wallet/drivers"
            element={
              <AdminRoute>
                <AdminLayout>
                  <WalletDrivers />
                </AdminLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/wallet/transactions"
            element={
              <AdminRoute>
                <AdminLayout>
                  <WalletTransactions />
                </AdminLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/wallet/withdrawals"
            element={
              <AdminRoute>
                <AdminLayout>
                  <PendingWithdrawals />
                </AdminLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/coupons"
            element={
              <AdminRoute>
                <AdminLayout>
                  <CouponManagement />
                </AdminLayout>
              </AdminRoute>
            }
          />


          {/* Driver Routes */}
          <Route
            path="/driver/dashboard"
            element={
              <DriverRoute>
                <DriverDashboard />
              </DriverRoute>
            }
          />
          <Route
            path="/driver/profile"
            element={
              <DriverRoute>
                <DriverProfile />
              </DriverRoute>
            }
          />
          <Route
            path="/driver/current-ride"
            element={
              <DriverRoute>
                <CurrentRide />
              </DriverRoute>
            }
          />
          <Route
            path="/driver/ride-history"
            element={
              <DriverRoute>
                <RideHistory />
              </DriverRoute>
            }
          />
          <Route
            path="/driver/wallet/withdraw"
            element={
              <DriverRoute>
                <WalletWithdraw />
              </DriverRoute>
            }
          />
          <Route
            path="/driver/wallet/history"
            element={
              <DriverRoute>
                <WalletHistory />
              </DriverRoute>
            }
          />
          <Route
            path="/driver/wallet/commission"
            element={
              <DriverRoute>
                <WalletCommission />
              </DriverRoute>
            }
          />

          {/* 404 Not Found */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      {!pathname.includes("/admin") && <Footer />}
    </div>
  );
}

export default App;
